import React, { useEffect, useMemo, useState } from "react";
import {
  ActivityIndicator,
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
  Pressable,
} from "react-native";
import DropDownPicker from "react-native-dropdown-picker";
import { useSelector } from "react-redux";
import { BarChartControl } from "../../../components/custom/Charts/BarChartControl";
import { PieChartControl, PieSlice } from "../../../components/custom/Charts/PieChartControl";
import { usageL36M2Model } from "../../../models/reportsModel";
import { getUsageL36M2 } from "../../../services/inventoryService";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const CHART_COLORS = [
  "rgba(54, 162, 235, 0.8)",
  "rgba(255, 99, 132, 0.8)",
  "rgba(75, 192, 192, 0.8)",
];

const currentYear = new Date().getFullYear();
const LAST_3_YEARS = [currentYear - 2, currentYear - 1, currentYear];

function formatYAxisTick(value: number): string {
  if (value >= 1_000_000) return (value / 1_000_000).toFixed(1).replace(/\.0$/, "") + "M";
  if (value >= 1_000) return (value / 1_000).toFixed(1).replace(/\.0$/, "") + "k";
  return value.toLocaleString();
}

function toItems(values: string[]) {
  return values.sort().map((v) => ({ label: v, value: v }));
}

function buildYoYData(rows: usageL36M2Model[]) {
  const datasets = LAST_3_YEARS.map((year, i) => {
    const data = Array(12).fill(0);
    rows
      .filter((r) => r["Use Year"] === year)
      .forEach((r) => {
        const idx = (r["Use Month"] ?? 1) - 1;
        data[idx] = (data[idx] || 0) + (r.InvQty || 0);
      });
    return {
      label: String(year),
      data,
      backgroundColor: CHART_COLORS[i],
      borderColor: CHART_COLORS[i],
    };
  });
  return { labels: MONTHS, datasets };
}

function buildItemGroupPieData(rows: usageL36M2Model[], year: number): { slices: PieSlice[]; centerText: string } {
  const yearRows = rows.filter((r) => r["Use Year"] === year);

  const totals: Record<string, number> = {};
  yearRows.forEach((r) => {
    const grp = r["Item Group"] || "Unknown";
    totals[grp] = (totals[grp] || 0) + (r.InvQty || 0);
  });

  const total = Object.values(totals).reduce((s, v) => s + v, 0);
  if (total === 0) return { slices: [], centerText: "0" };

  const sorted = Object.entries(totals)
    .map(([label, value]) => ({ label, value, percentage: (value / total) * 100 }))
    .sort((a, b) => b.value - a.value);

  const TOP_N = 12;
  const top = sorted.slice(0, TOP_N);
  const rest = sorted.slice(TOP_N);

  if (rest.length > 0) {
    const otherVal = rest.reduce((s, x) => s + x.value, 0);
    top.push({ label: "Other", value: otherVal, percentage: (otherVal / total) * 100 });
  }

  const centerText = String(Object.keys(totals).length);

  return { slices: top, centerText };
}

interface CustomerRow {
  customer: string;
  currentQty: number;
  currentPct: number;
  lastPct: number;
}

function buildCustomerUsageData(rows: usageL36M2Model[], year: number): CustomerRow[] {
  const lastYear = year - 1;
  const curr: Record<string, number> = {};
  const last: Record<string, number> = {};

  rows.forEach((r) => {
    const name = r.Customer || "Unknown";
    if (r["Use Year"] === year) curr[name] = (curr[name] || 0) + (r.InvQty || 0);
    if (r["Use Year"] === lastYear) last[name] = (last[name] || 0) + (r.InvQty || 0);
  });

  const totalCurr = Object.values(curr).reduce((s, v) => s + v, 0);
  const totalLast = Object.values(last).reduce((s, v) => s + v, 0);
  const allCustomers = Array.from(new Set([...Object.keys(curr), ...Object.keys(last)]));

  return allCustomers
    .map((customer) => ({
      customer,
      currentQty: curr[customer] || 0,
      currentPct: totalCurr > 0 ? ((curr[customer] || 0) / totalCurr) * 100 : 0,
      lastPct: totalLast > 0 ? ((last[customer] || 0) / totalLast) * 100 : 0,
    }))
    .filter((r) => r.currentQty > 0)
    .sort((a, b) => b.currentQty - a.currentQty)
    .slice(0, 15);
}

// ── Table view ────────────────────────────────────────────────────────────────
const YoYTable = ({
  data,
}: {
  data: { labels: string[]; datasets: { label: string; data: number[]; borderColor: string }[] };
}) => {
  const [lastDs, currDs] = data.datasets.slice(-2);

  const yoyPct: (number | null)[] = data.labels.map((_, j) => {
    const last = (lastDs.data as number[])[j];
    const curr = (currDs.data as number[])[j];
    return last > 0 ? ((curr - last) / last) * 100 : null;
  });

  return (
    <ScrollView 
      horizontal 
      showsHorizontalScrollIndicator={true}
      contentContainerStyle={{ flexGrow: 1, minWidth: "100%" }}
    >
      <View style={{ flex: 1 }}>
        <View style={table.headerRow}>
          <View style={table.yearCell}>
            <Text style={table.headerText} />
          </View>
          {data.labels.map((m) => (
            <View key={m} style={table.cell}>
              <Text style={table.headerText}>{m}</Text>
            </View>
          ))}
        </View>
        {[lastDs, currDs].map((ds, i) => (
          <View key={ds.label} style={[table.row, i % 2 !== 0 && table.rowAlt]}>
            <View style={[table.yearCell, { borderLeftWidth: 3, borderLeftColor: ds.borderColor }]}>
              <Text style={table.yearText}>{ds.label}</Text>
            </View>
            {(ds.data as number[]).map((v, j) => (
              <View key={j} style={table.cell}>
                <Text style={table.cellText}>{v > 0 ? Math.round(v / 1000).toLocaleString() + "k" : "—"}</Text>
              </View>
            ))}
          </View>
        ))}
        <View style={[table.row, table.yoyRow]}>
          <View style={table.yearCell}>
            <Text style={table.yoyLabel}>YoY %</Text>
          </View>
          {yoyPct.map((pct, j) => (
            <View key={j} style={table.cell}>
              <Text style={[table.cellText, pct == null ? undefined : pct >= 0 ? table.yoyPos : table.yoyNeg]}>
                {pct != null ? (pct >= 0 ? "+" : "") + pct.toFixed(1) + "%" : "—"}
              </Text>
            </View>
          ))}
        </View>
      </View>
    </ScrollView>
  );
};

const table = StyleSheet.create({
  headerRow: { flexDirection: "row", backgroundColor: "#f0f4f8", borderBottomWidth: 1, borderBottomColor: "#ddd" },
  row: { flexDirection: "row", borderBottomWidth: 1, borderBottomColor: "#f0f0f0" },
  rowAlt: { backgroundColor: "#fafafa" },
  yearCell: { width: 60, paddingVertical: 10, paddingHorizontal: 8, justifyContent: "center" },
  cell: { flex: 1, minWidth: 50, paddingVertical: 10, paddingHorizontal: 6, alignItems: "flex-end" },
  headerText: { fontSize: 12, fontWeight: "600", color: "#555", textAlign: "center" },
  yearText: { fontSize: 13, fontWeight: "700", color: "#333" },
  cellText: { fontSize: 12, color: "#444" },
  yoyRow: { backgroundColor: "#f8f8f0" },
  yoyLabel: { fontSize: 12, fontWeight: "700", color: "#555" },
  yoyPos: { color: "#3BA272", fontWeight: "600" },
  yoyNeg: { color: "#EE6666", fontWeight: "600" },
});

// ── Usage by Customer table ───────────────────────────────────────────────────
const CustomerUsageTable = ({
  data,
  currentYear: year,
}: {
  data: CustomerRow[];
  currentYear: number;
}) => {
  const [expandedName, setExpandedName] = useState<string | null>(null);

  const handleToggle = (name: string) =>
    setExpandedName((prev) => (prev === name ? null : name));

  return (
    <ScrollView 
      horizontal 
      showsHorizontalScrollIndicator 
      contentContainerStyle={{ flexGrow: 1, minWidth: "100%" }}
    >
      <View style={{ flex: 1 }}>
        <View style={custTable.headerRow}>
          <Text style={[custTable.colCustomer, custTable.headerText]}>Customer</Text>
          <Text style={[custTable.colQty, custTable.headerText]}>{year} Qty</Text>
          <Text style={[custTable.colPct, custTable.headerText]}>{year} %</Text>
          <Text style={[custTable.colPct, custTable.headerText]}>{year - 1} %</Text>
        </View>
        {data.map((row, i) => (
          <View key={row.customer} style={[custTable.row, i % 2 !== 0 && custTable.rowAlt]}>
            <TouchableOpacity
              style={custTable.colCustomer}
              onPress={() => handleToggle(row.customer)}
              disabled={Platform.OS === "web"}
              activeOpacity={Platform.OS === "web" ? 1 : 0.2}
            >
              <View style={custTable.nameWrap}>
                <Text
                  style={[custTable.cellText, custTable.nameText]}
                  numberOfLines={Platform.OS === "web" ? undefined : (expandedName === row.customer ? undefined : 1)}
                  selectable={Platform.OS === "web" || expandedName === row.customer}
                >
                  {row.customer}
                </Text>
              </View>
            </TouchableOpacity>
            <Text style={[custTable.colQty, custTable.cellText]}>
              {Math.round(row.currentQty).toLocaleString()}
            </Text>
            <Text style={[custTable.colPct, custTable.cellText]}>
              {row.currentPct.toFixed(1)}%
            </Text>
            <Text style={[custTable.colPct, custTable.cellText]}>
              {row.lastPct.toFixed(1)}%
            </Text>
          </View>
        ))}
      </View>
    </ScrollView>
  );
};

const custTable = StyleSheet.create({
  headerRow: {
    flexDirection: "row", backgroundColor: "#f0f4f8",
    borderBottomWidth: 1, borderBottomColor: "#ddd", paddingVertical: 8
  },
  row: {
    flexDirection: "row", borderBottomWidth: 1,
    borderBottomColor: "#f0f0f0", paddingVertical: 7, alignItems: "center"
  },
  rowAlt: { backgroundColor: "#fafafa" },
  headerText: { fontSize: 11, fontWeight: "600", color: "#555" },
  cellText: { fontSize: 12, color: "#444" },
  colCustomer: { flex: 1, minWidth: 200, paddingHorizontal: 8, justifyContent: "center" },
  colQty: { width: 110, textAlign: "right", paddingHorizontal: 6 },
  colPct: { width: 68, textAlign: "right", paddingHorizontal: 6 },
  nameWrap: { flexDirection: "row", alignItems: "flex-start" },
  nameText: { flex: 1 },
});

// ── Filter dropdown wrapper ───────────────────────────────────────────────────
interface FilterDropdownProps {
  label: string;
  value: string[];
  onChange: React.Dispatch<React.SetStateAction<string[]>>;
  items: { label: string; value: string }[];
  open?: boolean;
  setOpen?: (o: boolean) => void;
  zIndex?: number;
  zIndexInverse?: number;
  onOpen?: () => void;
}

const FilterDropdown: React.FC<FilterDropdownProps> = ({
  label, value, onChange, items,
  open, setOpen, zIndex, zIndexInverse, onOpen,
}) => (
  <View style={[filterItemStyle, { zIndex }]}>
    {Platform.OS === "web" && (
      <style type="text/css">{`
        [data-hoveritem="true"]:hover, [data-hover-item="true"]:hover {
          background-color: #f5f5f5 !important;
        }
      `}</style>
    )}
    <Text style={filterLabelStyle}>{label}</Text>
    <DropDownPicker
      multiple={true}
      mode="BADGE"
      open={open!}
      setOpen={setOpen!}
      items={items}
      value={value}
      setValue={onChange as any}
      placeholder="All"
      listMode="SCROLLVIEW"
      maxHeight={600}
      style={{ borderColor: "#ddd", borderRadius: 6, minHeight: 38 }}
      dropDownContainerStyle={{ borderColor: "#ddd", borderRadius: 6 }}
      zIndex={zIndex}
      zIndexInverse={zIndexInverse}
      onOpen={onOpen}
      badgeDotColors={["#1976d2"]}
      searchable={true}
      searchPlaceholder="Search..."
      searchContainerStyle={{ borderBottomColor: "#f0f0f0", borderBottomWidth: 1, padding: 10 }}
      searchTextInputStyle={{ borderColor: "#ddd", borderWidth: 1, borderRadius: 6, height: 36, paddingHorizontal: 12, fontSize: 13 }}
      itemProps={{ dataSet: { hoveritem: "true" } } as any}
      dropDownDirection="BOTTOM"
    />
  </View>
);

const filterItemStyle = { minWidth: 160, flex: 1 } as const;
const filterLabelStyle = { fontSize: 12, color: "#555", marginBottom: 4, fontWeight: "500" as const };

// ── Main screen ───────────────────────────────────────────────────────────────
export default function SalesScorecardScreen() {
  const { usageL36M2Report, isUsageL36M2ReportListLoaded } = useSelector(
    (state: any) => state.reports
  );
  const rows: usageL36M2Model[] = usageL36M2Report;

  // #UsageL36M2 No app-level guard — API is called on every screen mount; relies on API cache to avoid re-executing the SP
  useEffect(() => {
    getUsageL36M2();
  }, []);

  // native-only open states
  const [groupOpen, setGroupOpen] = useState(false);
  const [dispatchOpen, setDispatchOpen] = useState(false);
  const [deliveryOpen, setDeliveryOpen] = useState(false);
  const [slpOpen, setSlpOpen] = useState(false);
  const [itemGroupOpen, setItemGroupOpen] = useState(false);

  const [groupVal, setGroupVal] = useState<string[]>([]);
  const [dispatchVal, setDispatchVal] = useState<string[]>([]);
  const [deliveryVal, setDeliveryVal] = useState<string[]>([]);
  const [slpVal, setSlpVal] = useState<string[]>([]);
  const [itemGroupVal, setItemGroupVal] = useState<string[]>([]);
  const [pieYear, setPieYear] = useState(currentYear);

  const groupItems = useMemo(() => toItems(Array.from(new Set(rows.map((r) => r.ItmsGrpNam).filter(Boolean)))), [rows]);
  const dispatchItems = useMemo(() => toItems(Array.from(new Set(rows.map((r) => r.State).filter(Boolean)))), [rows]);
  const deliveryItems = useMemo(() => toItems(Array.from(new Set(rows.map((r) => r["Delivery State"]).filter(Boolean)))), [rows]);
  const slpItems = useMemo(() => toItems(Array.from(new Set(rows.map((r) => r.SlpName).filter(Boolean)))), [rows]);
  const itemGroupItems = useMemo(() => toItems(Array.from(new Set(rows.map((r) => r["Item Group"]).filter(Boolean)))), [rows]);

  const filtered = useMemo(() => {
    return rows.filter((r) => {
      if (!LAST_3_YEARS.includes(r["Use Year"])) return false;
      if (groupVal.length > 0 && !groupVal.includes(r.ItmsGrpNam)) return false;
      if (dispatchVal.length > 0 && !dispatchVal.includes(r.State)) return false;
      if (deliveryVal.length > 0 && !deliveryVal.includes(r["Delivery State"])) return false;
      if (slpVal.length > 0 && !slpVal.includes(r.SlpName)) return false;
      if (itemGroupVal.length > 0 && !itemGroupVal.includes(r["Item Group"])) return false;
      return true;
    });
  }, [rows, groupVal, dispatchVal, deliveryVal, slpVal, itemGroupVal]);

  const chartData = useMemo(() => buildYoYData(filtered), [filtered]);
  const pieData = useMemo(() => buildItemGroupPieData(filtered, pieYear), [filtered, pieYear]);
  const customerUsageData = useMemo(() => buildCustomerUsageData(filtered, currentYear), [filtered]);

  const maxVal = useMemo(() => Math.max(...chartData.datasets.flatMap((d) => d.data as number[]), 1), [chartData]);
  const stepSize = useMemo(() => {
    const mag = Math.pow(10, Math.floor(Math.log10(maxVal)));
    return Math.ceil(maxVal / (5 * mag)) * mag;
  }, [maxVal]);

  // #UsageL36M2 Loading UI gate — shows spinner while data hasn't arrived; does not prevent the API call
  if (!isUsageL36M2ReportListLoaded) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="#1976d2" />
        <Text style={styles.loadingText}>Loading Sales Scorecard...</Text>
      </View>
    );
  }

  const anyOpen = groupOpen || dispatchOpen || deliveryOpen || slpOpen || itemGroupOpen;
  const closeAllDropdowns = () => {
    setGroupOpen(false);
    setDispatchOpen(false);
    setDeliveryOpen(false);
    setSlpOpen(false);
    setItemGroupOpen(false);
  };

  const resetFilters = () => {
    setGroupVal([]);
    setDispatchVal([]);
    setDeliveryVal([]);
    setSlpVal([]);
    setItemGroupVal([]);
  };

  return (
    <ScrollView
      style={styles.container}
      contentContainerStyle={styles.content}
      scrollEnabled={!anyOpen}
    >
      {anyOpen && (
        <Pressable
          style={{ position: Platform.OS === "web" ? ("fixed" as any) : "absolute", top: 0, left: 0, right: 0, bottom: 0, zIndex: 999, height: Platform.OS === "web" ? "100vh" : 2000 }}
          onPress={closeAllDropdowns}
        />
      )}

      {/* Filters */}
      <View style={styles.filterPanel}>
        <View style={styles.filterHeaderRow}>
          <Text style={styles.filterHeading}>Filters</Text>
          <TouchableOpacity onPress={resetFilters} style={styles.resetBtn}>
            <Text style={styles.resetBtnText}>Reset Filter</Text>
          </TouchableOpacity>
        </View>
        <View style={styles.filterGrid}>
          <FilterDropdown
            label="Item Category"
            value={groupVal} onChange={setGroupVal} items={groupItems}
            open={groupOpen} setOpen={setGroupOpen} zIndex={600} zIndexInverse={100}
            onOpen={() => { setDispatchOpen(false); setDeliveryOpen(false); setSlpOpen(false); setItemGroupOpen(false); }}
          />
          <FilterDropdown
            label="Dispatch State"
            value={dispatchVal} onChange={setDispatchVal} items={dispatchItems}
            open={dispatchOpen} setOpen={setDispatchOpen} zIndex={500} zIndexInverse={200}
            onOpen={() => { setGroupOpen(false); setDeliveryOpen(false); setSlpOpen(false); setItemGroupOpen(false); }}
          />
          <FilterDropdown
            label="Delivery State"
            value={deliveryVal} onChange={setDeliveryVal} items={deliveryItems}
            open={deliveryOpen} setOpen={setDeliveryOpen} zIndex={400} zIndexInverse={300}
            onOpen={() => { setGroupOpen(false); setDispatchOpen(false); setSlpOpen(false); setItemGroupOpen(false); }}
          />
          <FilterDropdown
            label="Sales Person"
            value={slpVal} onChange={setSlpVal} items={slpItems}
            open={slpOpen} setOpen={setSlpOpen} zIndex={300} zIndexInverse={400}
            onOpen={() => { setGroupOpen(false); setDispatchOpen(false); setDeliveryOpen(false); setItemGroupOpen(false); }}
          />
          <FilterDropdown
            label="Item Group"
            value={itemGroupVal} onChange={setItemGroupVal} items={itemGroupItems}
            open={itemGroupOpen} setOpen={setItemGroupOpen} zIndex={200} zIndexInverse={500}
            onOpen={() => { setGroupOpen(false); setDispatchOpen(false); setDeliveryOpen(false); setSlpOpen(false); }}
          />
        </View>
      </View>

      {/* ── Three-column row ──────────────────────────────────────────────── */}
      <View style={styles.threeColRow}>

        {/* Left: Bar chart */}
        <View style={[styles.chartCard, styles.barCol]}>
          <Text style={styles.chartTitle}>Year On Year Usage</Text>
          <BarChartControl
            chartId="yoy-usage"
            chartHeight={200}
            xAxisLabel="Month"
            yAxisLabel="Qty"
            yAxisStepSize={stepSize}
            yAxisTickCallback={formatYAxisTick}
            dataRecords={chartData}
            legendLocation="bottom"
            hasXAxisGridlines={false}
            isYAxisBeginAtZero={true}
          />
        </View>

        {/* Middle: Donut chart */}
        <View style={[styles.chartCard, styles.pieCol]}>
          <Text style={styles.chartTitle}>{pieYear} Usage by Item Group</Text>
          <View style={styles.yearTabs}>
            {LAST_3_YEARS.map((y) => (
              <TouchableOpacity
                key={y}
                style={[styles.yearTab, pieYear === y && styles.yearTabActive]}
                onPress={() => setPieYear(y)}
              >
                <Text style={[styles.yearTabText, pieYear === y && styles.yearTabTextActive]}>
                  {y}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
          <PieChartControl
            title=""
            slices={pieData.slices}
            centerText={pieData.centerText}
          />
        </View>

        {/* Right: Customer table */}
        <View style={[styles.chartCard, styles.custCol]}>
          <Text style={styles.chartTitle}>
            Usage by Customer — {currentYear} vs {currentYear - 1}
          </Text>
          <CustomerUsageTable data={customerUsageData} currentYear={currentYear} />
        </View>

      </View>

      {/* ── Monthly YoY table — full width ────────────────────────────────── */}
      <View style={[styles.chartCard, styles.cardSpaced]}>
        <Text style={styles.chartTitle}>
          Monthly Usage ({LAST_3_YEARS.join(" / ")})
        </Text>
        <YoYTable data={chartData} />
      </View>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: "#f5f5f5" },
  content: { padding: 16, paddingBottom: 40 },
  loadingContainer: { flex: 1, justifyContent: "center", alignItems: "center", gap: 12 },
  loadingText: { fontSize: 14, color: "#666" },
  pageTitle: { fontSize: 18, fontWeight: "600", marginBottom: 12, color: "#1976d2" },
  filterPanel: {
    backgroundColor: "#fff", borderRadius: 8, padding: 14, marginBottom: 16,
    shadowColor: "#000", shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.08, shadowRadius: 3, elevation: 2,
    zIndex: 1000,
  },
  filterHeaderRow: { flexDirection: "row", justifyContent: "space-between", alignItems: "center", marginBottom: 10 },
  filterHeading: {
    fontSize: 12, fontWeight: "600", color: "#888",
    textTransform: "uppercase", letterSpacing: 0.5,
  },
  resetBtn: { paddingHorizontal: 10, paddingVertical: 4, borderRadius: 4, backgroundColor: "#f0f0f0" },
  resetBtnText: { fontSize: 11, color: "#555", fontWeight: "600" as const },
  filterGrid: { flexDirection: "row", flexWrap: "wrap", gap: 12, zIndex: 1000 },
  searchInput: {
    borderWidth: 1, borderColor: "#ddd", borderRadius: 6,
    paddingHorizontal: 10, paddingVertical: 8,
    fontSize: 13, backgroundColor: "#fff", height: 38,
  },
  chartCard: {
    backgroundColor: "#fff", borderRadius: 8, padding: 16,
    shadowColor: "#000", shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.08, shadowRadius: 3, elevation: 2,
  },
  chartTitle: { fontSize: 15, fontWeight: "600", color: "#333", marginBottom: 12 },
  sectionDivider: { height: 1, backgroundColor: "#e8e8e8", marginVertical: 16 },
  cardSpaced: { marginBottom: 16 },
  threeColRow: { flexDirection: "row", gap: 16, marginBottom: 16, alignItems: "stretch" },
  barCol: { flex: 1.6 },
  pieCol: { flex: 1 },
  custCol: { flex: 1.4 },
  yearTabs: { flexDirection: "row", gap: 8, marginBottom: 14 },
  yearTab: {
    paddingHorizontal: 14, paddingVertical: 6, borderRadius: 16,
    borderWidth: 1, borderColor: "#ddd", backgroundColor: "#f5f5f5"
  },
  yearTabActive: { backgroundColor: "#C49A1E", borderColor: "#C49A1E" },
  yearTabText: { fontSize: 13, color: "#555", fontWeight: "500" as const },
  yearTabTextActive: { color: "#fff", fontWeight: "700" as const },
});
